'use client';

import Link from 'next/link';
import { PageHeader } from '@/shared/layout';
import {
  Building2, TrendingUp, TrendingDown, Clock, Tag,
  Quote, ArrowRight, BarChart3, MapPin, BedDouble,
  type LucideIcon,
} from 'lucide-react';
import { usePageContent } from '@/core/i18n/content';
import { CASE_STUDIES_CONTENT } from '@/core/i18n/content/case-studies.content';
import type { CaseStudyEntry, CaseStudyMetric } from '@/core/i18n/content/case-studies.types';
import { cn } from '@/core/utils/cn';

/* -------------------------------------------------------------------------- */
/*  Stat icons                                                                 */
/* -------------------------------------------------------------------------- */

const OVERVIEW_ICONS: LucideIcon[] = [TrendingUp, TrendingDown, BarChart3, Building2];

/* -------------------------------------------------------------------------- */
/*  MetricRow                                                                  */
/* -------------------------------------------------------------------------- */

function MetricRow({
  metric, beforeLabel, afterLabel, idx, studySlug,
}: {
  metric: CaseStudyMetric; beforeLabel: string; afterLabel: string; idx: number; studySlug: string;
}) {
  const isPositive = metric.change.startsWith('+');
  return (
    <div
      id={`cs-metric-${studySlug}-${idx}`}
      className={cn(
        'grid grid-cols-4 gap-3 items-center py-3 px-4 rounded-lg text-sm',
        idx % 2 === 0 ? 'bg-hms-surface/50' : 'bg-transparent',
      )}
    >
      <span className="text-hms-textSub font-medium col-span-1">{metric.label}</span>
      <span className="text-hms-textDim text-center">
        <span className="text-[10px] uppercase tracking-wider block text-hms-textDim/60 mb-0.5">{beforeLabel}</span>
        {metric.before}
      </span>
      <span className="text-hms-text text-center font-semibold">
        <span className="text-[10px] uppercase tracking-wider block text-hms-textDim/60 mb-0.5">{afterLabel}</span>
        {metric.after}
      </span>
      <span
        className={cn(
          'text-center font-bold text-sm',
          isPositive ? 'text-hms-green' : 'text-hms-teal',
        )}
      >
        {metric.change}
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  CaseStudyCard                                                              */
/* -------------------------------------------------------------------------- */

function CaseStudyCard({
  study, content,
}: {
  study: CaseStudyEntry;
  content: {
    challengeLabel: string;
    solutionLabel: string;
    metricsLabel: string;
    beforeLabel: string;
    afterLabel: string;
    implementationLabel: string;
    tagsLabel: string;
  };
}) {
  return (
    <article id={`cs-study-${study.slug}`} className="space-y-6">
      {/* Hotel header */}
      <div className="rounded-xl border border-hms-border bg-hms-card p-6">
        <div className="flex items-start gap-4">
          <div
            id={`cs-logo-${study.slug}`}
            className="w-14 h-14 rounded-xl bg-gradient-to-br from-hms-gold to-hms-goldLt flex items-center justify-center shrink-0 shadow-md shadow-hms-gold/20"
          >
            <span className="text-lg font-bold text-black tracking-tight">{study.logo}</span>
          </div>
          <div className="flex-1 min-w-0">
            <h2 id={`cs-name-${study.slug}`} className="text-xl font-bold text-hms-text">
              {study.hotelName}
            </h2>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-sm text-hms-textSub">
              <span className="flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-hms-gold" />
                {study.location}
              </span>
              <span className="flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5 text-hms-gold" />
                {study.propertyType}
              </span>
              <span className="flex items-center gap-1.5">
                <BedDouble className="h-3.5 w-3.5 text-hms-gold" />
                {study.roomCount}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Challenge & Solution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <section
          id={`cs-challenge-${study.slug}`}
          className="rounded-xl border border-hms-border bg-hms-card p-6 space-y-3"
        >
          <h3 className="text-base font-semibold text-hms-red flex items-center gap-2">
            <TrendingDown className="h-4 w-4" />
            {content.challengeLabel}
          </h3>
          <p className="text-sm text-hms-textSub leading-relaxed">{study.challenge}</p>
        </section>
        <section
          id={`cs-solution-${study.slug}`}
          className="rounded-xl border border-hms-border bg-hms-card p-6 space-y-3"
        >
          <h3 className="text-base font-semibold text-hms-green flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            {content.solutionLabel}
          </h3>
          <p className="text-sm text-hms-textSub leading-relaxed">{study.solution}</p>
        </section>
      </div>

      {/* Metrics table */}
      <section
        id={`cs-metrics-${study.slug}`}
        className="rounded-xl border border-hms-border bg-hms-card p-6 space-y-4"
      >
        <h3 className="text-base font-semibold text-hms-text flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-hms-gold" />
          {content.metricsLabel}
        </h3>
        <div className="space-y-1">
          {study.metrics.map((m, idx) => (
            <MetricRow
              key={idx}
              metric={m}
              beforeLabel={content.beforeLabel}
              afterLabel={content.afterLabel}
              idx={idx}
              studySlug={study.slug}
            />
          ))}
        </div>
      </section>

      {/* Implementation time & tags */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div
          id={`cs-impl-${study.slug}`}
          className="rounded-xl border border-hms-border bg-hms-card p-6 flex items-center gap-4"
        >
          <Clock className="h-5 w-5 text-hms-gold shrink-0" />
          <div>
            <p className="text-xs text-hms-textDim uppercase tracking-wider">{content.implementationLabel}</p>
            <p className="text-lg font-bold text-hms-text mt-0.5">{study.implementationTime}</p>
          </div>
        </div>
        <div
          id={`cs-tags-${study.slug}`}
          className="rounded-xl border border-hms-border bg-hms-card p-6"
        >
          <p className="text-xs text-hms-textDim uppercase tracking-wider flex items-center gap-1.5 mb-3">
            <Tag className="h-3.5 w-3.5" />
            {content.tagsLabel}
          </p>
          <div className="flex flex-wrap gap-2">
            {study.tags.map((tag) => (
              <span
                key={tag}
                className="px-2.5 py-1 rounded-md bg-hms-gold/10 text-hms-gold text-xs font-medium border border-hms-gold/20"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Testimonial */}
      <blockquote
        id={`cs-testimonial-${study.slug}`}
        className="rounded-xl border border-hms-gold/20 bg-gradient-to-br from-hms-gold/5 to-transparent p-6 space-y-4"
      >
        <Quote className="h-6 w-6 text-hms-gold/40" />
        <p className="text-sm text-hms-text leading-relaxed italic">
          &ldquo;{study.testimonial.quote}&rdquo;
        </p>
        <footer className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-hms-gold/20 flex items-center justify-center">
            <span className="text-sm font-bold text-hms-gold">
              {study.testimonial.author.split(' ').map((n) => n[0]).join('')}
            </span>
          </div>
          <div>
            <p className="text-sm font-semibold text-hms-text">{study.testimonial.author}</p>
            <p className="text-xs text-hms-textSub">{study.testimonial.role}</p>
          </div>
        </footer>
      </blockquote>
    </article>
  );
}

/* -------------------------------------------------------------------------- */
/*  CaseStudiesPage                                                            */
/* -------------------------------------------------------------------------- */

export function CaseStudiesPage() {
  const c = usePageContent(CASE_STUDIES_CONTENT);

  return (
    <div className="min-h-screen bg-hms-bg">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <PageHeader title={c.header.title} description={c.header.description} />

        {/* Overview stats */}
        <section id="cs-overview" className="mt-8 space-y-4">
          <h2 className="text-lg font-semibold text-hms-text">{c.overviewTitle}</h2>
          <p className="text-sm text-hms-textSub max-w-2xl">{c.overviewDescription}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            {c.overviewStats.map((stat, idx) => {
              const Icon = OVERVIEW_ICONS[idx] ?? BarChart3;
              return (
                <div
                  key={idx}
                  id={`cs-overview-stat-${idx}`}
                  className="rounded-xl border border-hms-border bg-hms-card p-5 text-center space-y-2"
                >
                  <Icon className="h-5 w-5 text-hms-gold mx-auto" />
                  <p className="text-2xl font-bold text-hms-text tracking-tight">{stat.value}</p>
                  <p className="text-xs text-hms-textSub">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Case studies */}
        <div className="mt-12 space-y-16">
          {c.studies.map((study) => (
            <CaseStudyCard
              key={study.slug}
              study={study}
              content={{
                challengeLabel: c.challengeLabel,
                solutionLabel: c.solutionLabel,
                metricsLabel: c.metricsLabel,
                beforeLabel: c.beforeLabel,
                afterLabel: c.afterLabel,
                implementationLabel: c.implementationLabel,
                tagsLabel: c.tagsLabel,
              }}
            />
          ))}
        </div>

        {/* CTA */}
        <section
          id="cs-cta"
          className="mt-16 rounded-xl border border-hms-gold/20 bg-gradient-to-br from-hms-gold/5 via-hms-card to-hms-card p-8 text-center space-y-4"
        >
          <h2 className="text-xl font-bold text-hms-text">{c.ctaTitle}</h2>
          <p className="text-sm text-hms-textSub max-w-lg mx-auto">{c.ctaDescription}</p>
          <Link
            id="cs-cta-btn"
            href="/contact"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-gradient-to-r from-hms-gold to-hms-goldLt text-black font-semibold text-sm shadow-md shadow-hms-gold/25 hover:shadow-hms-gold/40 transition-all duration-300"
          >
            {c.ctaButton}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </div>
    </div>
  );
}
